import React, { useState } from 'react';
import {
  Mail,
  FileText,
  Calendar,
  MessageCircle,
  Globe,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Sparkles,
  X,
} from 'lucide-react';
import type { PersonalSource, WebSource, ChatSource } from '../services/chatService';

interface SourcesPanelProps {
  sources: ChatSource[];
  isVisible: boolean;
  onClose?: () => void;
  /** Whether this is rendered as a mobile drawer overlay */
  isMobileDrawer?: boolean;
}

const SOURCE_ICONS: Record<string, React.ElementType> = {
  email: Mail,
  file: FileText,
  calendar: Calendar,
  chat: MessageCircle,
  web: Globe,
};

function getSourceIcon(source: ChatSource) {
  if (source.type === 'web') return Globe;
  const personal = source as PersonalSource;
  return SOURCE_ICONS[personal.sourceType || ''] || BookOpen;
}

function SourceCard({ source, index }: { source: ChatSource; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getSourceIcon(source);
  const hasSnippet = !!source.snippet;

  const title = source.type === 'web'
    ? source.title
    : (source as PersonalSource).subject || (source as PersonalSource).title || (source as PersonalSource).fileName || 'Untitled';

  const meta = source.type === 'web'
    ? (() => { try { return new URL(source.url).hostname.replace('www.', ''); } catch { return source.url; } })()
    : (source as PersonalSource).from || (source as PersonalSource).author || (source as PersonalSource).date || '';

  return (
    <div
      className="animate-fadeIn"
      style={{ animationDelay: `${index * 40}ms`, animationFillMode: 'both' }}
    >
      {/* Compact single-line row */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/70 transition-colors ${expanded ? 'bg-white/50' : ''}`}>
        {/* Icon badge */}
        <div className={`
          flex-shrink-0 w-5 h-5 rounded flex items-center justify-center
          ${source.type === 'web' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-niwt-primary'}
        `}>
          <Icon className="w-3 h-3" />
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          {source.type === 'web' ? (
            <a
              href={(source as WebSource).url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-medium text-gray-800 hover:text-blue-700 transition-colors"
            >
              <span className="truncate">{title}</span>
              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-0 group-hover:opacity-60" />
            </a>
          ) : (
            <p className="text-xs font-medium text-gray-800 truncate">{title}</p>
          )}
          {meta && (
            <p className="text-[10px] text-gray-400 truncate leading-tight">{meta}</p>
          )}
        </div>

        {/* Expand toggle */}
        {hasSnippet && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 p-0.5 text-gray-300 hover:text-gray-600 transition-colors"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded
              ? <ChevronDown className="w-3 h-3" />
              : <ChevronRight className="w-3 h-3" />
            }
          </button>
        )}
      </div>

      {/* Expandable snippet */}
      {hasSnippet && expanded && (
        <div className="px-3 pb-2 pt-0">
          <div className="ml-7 pl-2 border-l-2 border-gray-100">
            <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-3">
              {source.snippet}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const SourcesPanel: React.FC<SourcesPanelProps> = ({
  sources,
  isVisible,
  onClose,
  isMobileDrawer = false,
}) => {
  if (!isVisible) return null;

  const personalSources = sources.filter(s => s.type === 'personal') as PersonalSource[];
  const webSources = sources.filter(s => s.type === 'web') as WebSource[];
  const hasSources = sources.length > 0;

  const panelContent = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-niwt-primary" />
            <h3 className="text-xs font-semibold text-gray-900 tracking-tight">Sources</h3>
            {hasSources && (
              <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                {sources.length}
              </span>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {hasSources && (
          <p className="text-[10px] text-gray-400 mt-0.5">
            What informed this response
          </p>
        )}
      </div>

      {/* Source list */}
      <div className="flex-1 overflow-y-auto py-1.5">
        {!hasSources ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center mb-3">
              <BookOpen className="w-4 h-4 text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">No sources</p>
            <p className="text-xs text-gray-300 mt-1">
              This response was based on general conversation
            </p>
          </div>
        ) : (
          <div>
            {/* Personal sources */}
            {personalSources.length > 0 && (
              <div>
                {webSources.length > 0 && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-3 pt-1 pb-0.5">
                    Personal
                  </p>
                )}
                {personalSources.map((source, idx) => (
                  <SourceCard key={`personal-${idx}`} source={source} index={idx} />
                ))}
              </div>
            )}

            {/* Web sources */}
            {webSources.length > 0 && (
              <div>
                {personalSources.length > 0 && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-3 pt-2 pb-0.5">
                    Web
                  </p>
                )}
                {webSources.map((source, idx) => (
                  <SourceCard
                    key={`web-${idx}`}
                    source={source}
                    index={personalSources.length + idx}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile drawer: overlay
  if (isMobileDrawer) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden animate-fadeIn"
          onClick={onClose}
        />
        {/* Drawer */}
        <div className="fixed right-0 top-0 bottom-0 w-72 bg-white shadow-2xl z-50 lg:hidden animate-slideIn">
          {panelContent}
        </div>
      </>
    );
  }

  // Desktop: inline panel (flex sibling inside main)
  return (
    <aside className="bg-gray-50 flex flex-col overflow-hidden h-full">
      {panelContent}
    </aside>
  );
};

export default SourcesPanel;
